const STORAGE_KEY = 'avari_marketing_attribution_v1';
const COOKIE_KEY = 'avari_marketing_attribution';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180;

const UTM_KEYS = {
    utm_source: 'source',
    utm_medium: 'medium',
    utm_campaign: 'campaign',
    utm_content: 'content',
    utm_term: 'term',
    utm_id: 'id'
};

const CLICK_ID_KEYS = [
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'msclkid',
    'ttclid',
    'li_fat_id'
];

const SEARCH_HOSTS = [
    ['google.', 'google'],
    ['bing.', 'bing'],
    ['yahoo.', 'yahoo'],
    ['duckduckgo.', 'duckduckgo'],
    ['ecosia.', 'ecosia']
];

function clean(value, limit = 1000) {
    if (value == null) return '';
    return Array.from(String(value).trim())
        .filter((character) => {
            const codePoint = character.codePointAt(0);
            return codePoint > 0x1f && codePoint !== 0x7f;
        })
        .join('')
        .slice(0, limit);
}

function safeUrlWithoutHash(url) {
    try {
        const parsed = new URL(url);
        parsed.hash = '';
        return parsed.toString();
    } catch {
        return clean(url);
    }
}

function parseStoredValue(raw) {
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
}

function readStoredCookie() {
    try {
        const prefix = `${COOKIE_KEY}=`;
        const cookie = String(document.cookie || '')
            .split(';')
            .map((part) => part.trim())
            .find((part) => part.startsWith(prefix));

        return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : '';
    } catch {
        return '';
    }
}

function parseStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = parseStoredValue(raw);
        if (parsed) return parsed;
    } catch {
        // Fall through to the cookie when storage is unavailable.
    }

    return parseStoredValue(readStoredCookie()) || {};
}

function writeStored(value) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
        // Storage can be unavailable in private or locked-down browsers.
    }

    try {
        document.cookie = `${COOKIE_KEY}=${encodeURIComponent(JSON.stringify(value))}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
    } catch {
        // Cookies are best effort; checkout submits localStorage-backed data.
    }
}

function getExternalReferrer() {
    const referrer = document.referrer || '';
    if (!referrer) return '';

    try {
        const referrerUrl = new URL(referrer);
        if (referrerUrl.hostname === window.location.hostname) {
            return '';
        }
    } catch {
        return clean(referrer);
    }

    return referrer;
}

function deriveSourceMedium(params, externalReferrer) {
    const explicitSource = clean(params.get('utm_source'), 120);
    const explicitMedium = clean(params.get('utm_medium'), 120);

    if (explicitSource || explicitMedium) {
        return {
            source: explicitSource || 'unknown',
            medium: explicitMedium || 'unknown'
        };
    }

    if (params.get('gclid') || params.get('gbraid') || params.get('wbraid')) {
        return { source: 'google', medium: 'paid_search' };
    }

    if (params.get('msclkid')) {
        return { source: 'bing', medium: 'paid_search' };
    }

    if (params.get('fbclid')) {
        return { source: 'facebook', medium: 'paid_social' };
    }

    if (params.get('ttclid')) {
        return { source: 'tiktok', medium: 'paid_social' };
    }

    if (params.get('li_fat_id')) {
        return { source: 'linkedin', medium: 'paid_social' };
    }

    if (externalReferrer) {
        try {
            const host = new URL(externalReferrer).hostname
                .replace(/^www\./, '')
                .toLowerCase();
            const searchMatch = SEARCH_HOSTS.find(([needle]) =>
                host.includes(needle)
            );
            if (searchMatch) {
                return { source: searchMatch[1], medium: 'organic' };
            }
            return { source: host, medium: 'referral' };
        } catch {
            return { source: 'referral', medium: 'referral' };
        }
    }

    return { source: 'direct', medium: 'none' };
}

function buildTouch() {
    const params = new URLSearchParams(window.location.search);
    const externalReferrer = getExternalReferrer();
    const derived = deriveSourceMedium(params, externalReferrer);
    const touch = {
        capturedAt: new Date().toISOString(),
        landingPage: safeUrlWithoutHash(window.location.href),
        landingPath: `${window.location.pathname}${window.location.search}`,
        referrer: clean(externalReferrer),
        source: clean(derived.source, 120),
        medium: clean(derived.medium, 120),
        campaign: clean(params.get('utm_campaign'), 255),
        content: clean(params.get('utm_content'), 255),
        term: clean(params.get('utm_term'), 255),
        id: clean(params.get('utm_id'), 120),
        clickIds: {}
    };

    CLICK_ID_KEYS.forEach((key) => {
        const value = clean(params.get(key), 255);
        if (value) touch.clickIds[key] = value;
    });

    Object.keys(touch).forEach((key) => {
        if (
            touch[key] === '' ||
            (key === 'clickIds' && Object.keys(touch.clickIds).length === 0)
        ) {
            delete touch[key];
        }
    });

    return touch;
}

function hasMarketingSignal() {
    const params = new URLSearchParams(window.location.search);
    const hasTrackedQuery = [...Object.keys(UTM_KEYS), ...CLICK_ID_KEYS].some(
        (key) => params.has(key)
    );
    return hasTrackedQuery || !!getExternalReferrer();
}

function currentContext() {
    return {
        capturedAt: new Date().toISOString(),
        pageUrl: safeUrlWithoutHash(window.location.href),
        pagePath: `${window.location.pathname}${window.location.search}`,
        referrer: clean(document.referrer || '')
    };
}

export function initAttribution() {
    const stored = parseStored();
    const touch = buildTouch();
    const shouldUpdateLastTouch = hasMarketingSignal() || !stored.lastTouch;

    const next = {
        schemaVersion: '1',
        firstTouch: stored.firstTouch || touch,
        lastTouch: shouldUpdateLastTouch ? touch : stored.lastTouch,
        current: currentContext()
    };

    writeStored(next);
    window.__avariAttribution = {
        getPayload,
        reset
    };

    return next;
}

export function getPayload() {
    const stored = parseStored();
    if (!stored.firstTouch && !stored.lastTouch) {
        return initAttribution();
    }

    const next = {
        ...stored,
        schemaVersion: '1',
        current: currentContext()
    };
    writeStored(next);
    return next;
}

export function reset() {
    try {
        localStorage.removeItem(STORAGE_KEY);
        document.cookie = `${COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax`;
    } catch {
        // no-op
    }
}
